import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Divider,
  TextField,
  InputAdornment,
  Stack,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  ShoppingCart as ShoppingCartIcon,
  DateRange as DateIcon,
  Euro as EuroIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Note as NoteIcon
} from '@mui/icons-material';
import { apiService } from '../services/api';

const OrderSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('order'); // 'order', 'name', of 'amount'
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [amountError, setAmountError] = useState('');
  const [year, setYear] = useState('');
  const [years, setYears] = useState([]);

  useEffect(() => {
    // Haal beschikbare jaren op bij laden
    const fetchYears = async () => {
      try {
        const res = await apiService.getOrdersYears();
        setYears(res.data || []);
      } catch (e) {
        setYears([]);
      }
    };
    fetchYears();
  }, []);

  const handleSearch = async () => {
    setAmountError('');
    setError(null);
    setHasSearched(true);
    // Validatie
    if (searchType === 'order' || searchType === 'name') {
      if (!searchQuery.trim()) {
        setError('Voer een zoekterm in');
        return;
      }
    }
    if (searchType === 'amount') {
      if (!amountMin && !amountMax) {
        setAmountError('Vul minimaal één bedrag in');
        return;
      }
    }
    if ((amountMin && !/^\d+$/.test(amountMin)) || (amountMax && !/^\d+$/.test(amountMax))) {
      setAmountError('Alleen gehele bedragen zijn toegestaan');
      return;
    }
    if (amountMin && amountMax && parseInt(amountMin) > parseInt(amountMax)) {
      setAmountError('"Van" mag niet groter zijn dan "Tot"');
      return;
    }
    try {
      setLoading(true);
      let params = { per_page: 50 };
      if (searchType === 'order') {
        params = { ...params, search: searchQuery.trim(), search_type: 'order_number' };
      } else if (searchType === 'name') {
        params = { ...params, search: searchQuery.trim(), search_type: 'customer_name' };
      } else if (searchType === 'amount') {
        if (amountMin) params.amount_min = parseInt(amountMin);
        if (amountMax) params.amount_max = parseInt(amountMax);
        // zoek_type niet meesturen
      }
      // Jaarfilter toevoegen
      if (year) {
        params.date_from = `${year}-01-01`;
        params.date_to = `${year}-12-31`;
      }
      const response = await apiService.searchOrders(params);
      setOrders(response.data || []);
    } catch (err) {
      setError(err.message);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setOrders([]);
    setError(null);
    setHasSearched(false);
    setExpandedOrder(false);
  };

  const getOrderStatusColor = (status) => {
    const colors = {
      'pending': 'warning',
      'processing': 'info',
      'on-hold': 'error',
      'completed': 'success',
      'cancelled': 'default',
      'refunded': 'error',
      'failed': 'error',
      // Custom hiphot.nl statussen
      'intern-magazijn': 'info',
      'logo-ok': 'success',
      'logo-print': 'warning',
      'logo-proofprint': 'warning',
      'logo-received': 'info',
      'design-request': 'warning',
      'logo-request': 'warning'
    };
    return colors[status] || 'default';
  };

  const getOrderStatusLabel = (status) => {
    const labels = {
      'pending': 'In afwachting',
      'processing': 'In behandeling',
      'on-hold': 'Wacht',
      'completed': 'Afgerond',
      'cancelled': 'Geannuleerd',
      'refunded': 'Terugbetaald',
      'failed': 'Mislukt',
      // Custom hiphot.nl statussen
      'intern-magazijn': 'Magazijn HH',
      'logo-ok': 'Logo akkoord',
      'logo-print': 'Logo naar drukker',
      'logo-proofprint': 'Logo proefdruk',
      'logo-received': 'Logo ontvangen',
      'design-request': 'Design opvragen',
      'logo-request': 'Logo opvragen'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleAccordionChange = (orderId) => (event, isExpanded) => {
    setExpandedOrder(isExpanded ? orderId : false);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const OrderCard = ({ order }) => {
    // Helper function to get customer display name
    const getCustomerDisplayName = (billing) => {
      const fullName = `${billing?.first_name || ''} ${billing?.last_name || ''}`.trim();
      if (fullName) {
        return fullName;
      }
      return billing?.company || 'Onbekende klant';
    };

    const customerName = getCustomerDisplayName(order.billing);
    const customerEmail = order.billing?.email || 'Geen email';
    const customerNote = order.customer_note || order.note || null;
    
    return (
      <Accordion 
        expanded={expandedOrder === order.id} 
        onChange={handleAccordionChange(order.id)}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <Typography variant="h6" color="primary">
                Order #{order.number}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                ID: {order.id}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <Box display="flex" alignItems="center" mb={0.5}>
                <PersonIcon fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body1">
                  {customerName || 'Onbekende klant'}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                {customerEmail}
              </Typography>
              {order.billing?.company && (
                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                  {order.billing.company}
                </Typography>
              )}
            </Grid>
            
            <Grid item xs={12} sm={2}>
              <Box display="flex" alignItems="center" mb={0.5}>
                <DateIcon fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body2">
                  {formatDate(order.date_created)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={2}>
              <Box display="flex" alignItems="center" mb={0.5}>
                <EuroIcon fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  €{parseFloat(order.total).toFixed(2)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={2}>
              <Chip 
                label={getOrderStatusLabel(order.status)}
                color={getOrderStatusColor(order.status)}
                size="small"
              />
            </Grid>
          </Grid>
        </AccordionSummary>
        
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Customer Details */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Klantgegevens
              </Typography>
              <Paper sx={{ p: 2 }}>
                <Typography variant="body2"><strong>Naam:</strong> {customerName}</Typography>
                {order.billing?.company && (
                  <Typography variant="body2"><strong>Bedrijfsnaam:</strong> {order.billing.company}</Typography>
                )}
                <Typography variant="body2"><strong>Email:</strong> {customerEmail}</Typography>
                <Typography variant="body2"><strong>Telefoon:</strong> {order.billing?.phone || 'Niet opgegeven'}</Typography>
                
                <Divider sx={{ my: 1 }} />
                
                <Typography variant="body2"><strong>Factuuradres:</strong></Typography>
                <Typography variant="body2">
                  {order.billing?.address_1} {order.billing?.address_2}<br />
                  {order.billing?.postcode} {order.billing?.city}<br />
                  {order.billing?.country}
                </Typography>
                
                {order.shipping && order.shipping.address_1 && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2"><strong>Verzendadres:</strong></Typography>
                    <Typography variant="body2">
                      {order.shipping.address_1} {order.shipping.address_2}<br />
                      {order.shipping.postcode} {order.shipping.city}<br />
                      {order.shipping.country}
                    </Typography>
                  </>
                )}
              </Paper>
            </Grid>
            
            {/* Order Details */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Order Informatie
              </Typography>
              <Paper sx={{ p: 2 }}>
                <Typography variant="body2"><strong>Order nummer:</strong> #{order.number}</Typography>
                <Typography variant="body2"><strong>Order datum:</strong> {formatDate(order.date_created)}</Typography>
                <Typography variant="body2"><strong>Status:</strong> {getOrderStatusLabel(order.status)}</Typography>
                <Typography variant="body2"><strong>Betaalmethode:</strong> {order.payment_method_title || 'Niet opgegeven'}</Typography>
                <Typography variant="body2"><strong>Verzendmethode:</strong> {order.shipping_lines?.[0]?.method_title || 'Niet opgegeven'}</Typography>
                
                <Divider sx={{ my: 1 }} />
                
                <Typography variant="body2"><strong>Subtotaal:</strong> €{parseFloat(order.total - order.total_tax - order.shipping_total).toFixed(2)}</Typography>
                <Typography variant="body2"><strong>Verzendkosten:</strong> €{parseFloat(order.shipping_total).toFixed(2)}</Typography>
                <Typography variant="body2"><strong>BTW:</strong> €{parseFloat(order.total_tax).toFixed(2)}</Typography>
                <Typography variant="body1"><strong>Totaal:</strong> €{parseFloat(order.total).toFixed(2)}</Typography>
              </Paper>
            </Grid>

            {/* Customer Note */}
            {customerNote && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  <NoteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Notitie van de klant
                </Typography>
                <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {customerNote}
                  </Typography>
                </Paper>
              </Grid>
            )}
            
            {/* Order Items */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Bestelde Producten
              </Typography>
              <Paper>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell align="center">Aantal</TableCell>
                      <TableCell align="right">Prijs</TableCell>
                      <TableCell align="right">Totaal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.line_items?.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {item.name}
                          </Typography>
                          {item.meta_data && item.meta_data.length > 0 && (
                            <Typography variant="caption" color="textSecondary">
                              {item.meta_data.map(meta => {
                                // Special handling for COG fields
                                if (meta.key === '_alg_wc_cog_item_cost') {
                                  return `Costprice: €${parseFloat(meta.value).toFixed(2)}`;
                                } else if (meta.key === '_alg_wc_cog_item_profit') {
                                  return `Profit: €${parseFloat(meta.value).toFixed(2)}`;
                                } else {
                                  return `${meta.display_key}: ${meta.display_value}`;
                                }
                              }).join(', ')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.sku || 'Geen SKU'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {item.quantity}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            €{parseFloat(item.price).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            €{parseFloat(item.total).toFixed(2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Order Opzoeken
        </Typography>
      </Box>

      {/* Search Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Zoek Orders
          </Typography>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                select
                fullWidth
                label="Zoektype"
                value={searchType}
                onChange={(e) => {
                  setSearchType(e.target.value);
                  setSearchQuery('');
                  setAmountMin('');
                  setAmountMax('');
                  setAmountError('');
                  setYear('');
                }}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="order">Ordernummer</option>
                <option value="name">Klantnaam</option>
                <option value="amount">Bedrag</option>
              </TextField>
            </Grid>
            {searchType !== 'amount' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={searchType === 'order' ? 'Ordernummer' : 'Klantnaam'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={searchType === 'order' ? 'Bijv. 12345' : 'Voornaam en/of achternaam'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <Tooltip title="Zoekopdracht wissen">
                          <IconButton
                            size="small"
                            onClick={handleClearSearch}
                            edge="end"
                          >
                            <ClearIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}
            {searchType === 'amount' && (
              <>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    label="Bedrag Van"
                    value={amountMin}
                    onChange={e => setAmountMin(e.target.value.replace(/[^\d]/g, ''))}
                    inputProps={{ inputMode: 'numeric', pattern: '\\d*' }}
                    placeholder="Min"
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    label="Bedrag Tot"
                    value={amountMax}
                    onChange={e => setAmountMax(e.target.value.replace(/[^\d]/g, ''))}
                    inputProps={{ inputMode: 'numeric', pattern: '\\d*' }}
                    placeholder="Max"
                    size="small"
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSearch}
                disabled={loading || (searchType !== 'amount' && !searchQuery.trim())}
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              >
                {loading ? 'Zoeken...' : 'Zoeken'}
              </Button>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                select
                fullWidth
                label="Jaar"
                value={year}
                onChange={e => setYear(e.target.value)}
                SelectProps={{ native: true }}
                disabled={years.length === 0}
              >
                <option value="">Alle jaren</option>
                {years.length > 0 && years.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </TextField>
            </Grid>
          </Grid>
          {amountError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {amountError}
            </Typography>
          )}
          
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {searchType === 'order' && 'Zoek op ordernummer (bijv. 12345)'}
            {searchType === 'name' && 'Zoek op klantnaam (voornaam en/of achternaam)'}
            {searchType === 'amount' && 'Zoek op orderbedrag (gehele eurobedragen)'}
          </Typography>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Results */}
      {hasSearched && !loading && (
        <>
          {orders.length === 0 ? (
            <Box textAlign="center" py={8}>
              <ShoppingCartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Geen orders gevonden
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Probeer een andere zoekterm of controleer de spelling
              </Typography>
            </Box>
          ) : (
            <>
              <Alert severity="success" sx={{ mb: 3 }}>
                {orders.length} order{orders.length !== 1 ? 's' : ''} gevonden
              </Alert>

              {orders.map((order) => (
                <OrderCard key={order.id} order={order} />
              ))}
            </>
          )}
        </>
      )}

      {/* Initial State */}
      {!hasSearched && !loading && (
        <Box textAlign="center" py={8}>
          <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Zoek naar orders
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Voer een ordernummer of klantnaam in om orders te zoeken
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default OrderSearch;
